import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { POPOVER_TOUR_STEPS } from './popoverTourSteps.js'
import { useAdminModeOnboarding } from './useAdminModeOnboarding.js'

interface PopoverTourContextValue {
  currentStep: number | null
  totalSteps: number
  startTour: (fromStep?: number, autoTriggered?: boolean) => void
  nextStep: () => void
  skipTour: () => void
}

const PopoverTourContext = createContext<PopoverTourContextValue>({
  currentStep: null,
  totalSteps: POPOVER_TOUR_STEPS.length,
  startTour: () => {},
  nextStep: () => {},
  skipTour: () => {},
})

export function PopoverTourProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState<number | null>(null)
  const { completeTour, saveStepProgress } = useAdminModeOnboarding()
  const totalSteps = POPOVER_TOUR_STEPS.length
  const isAutoTriggeredRef = useRef(false)

  const finishTour = useCallback(() => {
    setCurrentStep(null)
    isAutoTriggeredRef.current = false
    completeTour()
  }, [completeTour])

  const startTour = useCallback((fromStep = 0, autoTriggered = false) => {
    isAutoTriggeredRef.current = autoTriggered
    // Delay by a frame so the Popover's positioning library can measure
    // the activator element before the popover becomes visible.
    requestAnimationFrame(() => {
      setCurrentStep(fromStep)
    })
  }, [])

  const nextStep = useCallback(() => {
    setCurrentStep(prev => {
      if (prev === null) {
        return null
      }
      if (prev >= totalSteps - 1) {
        finishTour()
        return null
      }
      const next = prev + 1
      if (next >= totalSteps - 1) {
        // User reached the last step — mark complete so they won't
        // see it again even if they navigate away without clicking "Got it".
        completeTour()
      } else if (isAutoTriggeredRef.current) {
        saveStepProgress(next)
      }
      return next
    })
  }, [completeTour, finishTour, saveStepProgress, totalSteps])

  const skipTour = useCallback(() => {
    finishTour()
  }, [finishTour])

  const value = useMemo(
    () => ({
      currentStep,
      totalSteps,
      startTour,
      nextStep,
      skipTour,
    }),
    [currentStep, totalSteps, startTour, nextStep, skipTour],
  )

  return (
    <PopoverTourContext.Provider value={value}>
      {children}
    </PopoverTourContext.Provider>
  )
}

export const usePopoverTour = () => useContext(PopoverTourContext)
